import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent, Button, Input } from '@heroui/react';
import { ChevronDown, Search } from 'lucide-react';

/**
 * Single-select Timezone picker with continent grouping + type-to-search.
 * Visual + structural parity with `FilterMultiSelect`: same Button trigger
 * (label above / value below / chevron), same Popover body (search input +
 * scrollable grouped list). Controlled component.
 *
 * @param timezones - [{ continent: 'America', zones: [{ id, label }] }]
 */
function FilterTimezoneSelect({
	timezones = [],
	value,
	onChange,
	label = 'Timezone',
	placeholder = 'Select timezone'
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const searchRef = useRef(null);

	const flatOptions = useMemo(() => {
		const out = [];
		(timezones || []).forEach(g =>
			(g.zones || []).forEach(z => {
				out.push({ id: String(z.id), label: z.label });
			})
		);
		return out;
	}, [timezones]);

	const selectedLabel = useMemo(() => {
		if (value == null || value === '') return placeholder;
		const hit = flatOptions.find(o => o.id === String(value));
		return hit ? hit.label : String(value);
	}, [value, flatOptions, placeholder]);

	const filteredGroups = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return timezones || [];
		return (timezones || [])
			.map(g => ({
				...g,
				zones: (g.zones || []).filter(
					z =>
						String(z.label).toLowerCase().includes(q) ||
						String(z.id).toLowerCase().includes(q)
				)
			}))
			.filter(g => (g.zones || []).length > 0);
	}, [timezones, search]);

	const handleOpenChange = useCallback(open => {
		setIsOpen(open);
		if (!open) setSearch('');
	}, []);

	useEffect(() => {
		if (!isOpen) return undefined;
		const t = setTimeout(() => {
			if (searchRef.current && typeof searchRef.current.focus === 'function') {
				searchRef.current.focus();
			}
		}, 0);
		return () => clearTimeout(t);
	}, [isOpen]);

	const handleSelect = useCallback(
		id => {
			if (typeof onChange === 'function') onChange(id);
			setIsOpen(false);
		},
		[onChange]
	);

	return (
		<Popover
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			placement="bottom-start"
			offset={6}
			shouldBlockScroll={false}
			classNames={{ content: 'p-0 overflow-hidden' }}
		>
			<PopoverTrigger>
				<Button
					variant="flat"
					disableRipple
					className="h-auto min-w-[180px] max-w-[220px] justify-between gap-2 rounded-medium border border-default-200 bg-default-50 px-3 py-2 hover:bg-default-100"
					aria-label={label}
				>
					<span className="flex min-w-0 flex-col items-start leading-tight">
						<span className="text-[11px] font-medium uppercase tracking-wide text-default-500">
							{label}
						</span>
						<span className="max-w-full truncate text-sm font-medium text-foreground">
							{selectedLabel}
						</span>
					</span>
					<ChevronDown size={14} className="shrink-0 text-default-500" aria-hidden="true" />
				</Button>
			</PopoverTrigger>

			<PopoverContent>
				<div
					className="flex w-[min(280px,calc(100vw-1rem))] flex-col"
					data-testid="filter-timezone-select"
				>
					<div className="border-b border-default-200 p-2">
						<Input
							ref={searchRef}
							size="sm"
							placeholder="Search..."
							value={search}
							onValueChange={setSearch}
							variant="flat"
							aria-label="Search timezones"
							startContent={<Search size={16} className="shrink-0 text-default-400" aria-hidden="true" />}
							isClearable
							onClear={() => setSearch('')}
						/>
					</div>

					<div className="max-h-[280px] overflow-y-auto p-2">
						{filteredGroups.length === 0 && (
							<div className="px-2 py-6 text-center text-xs text-default-400">
								{search
									? `No timezones match “${search}”`
									: 'No timezones available'}
							</div>
						)}

						{filteredGroups.map(group => (
							<div key={group.continent || '__u'} className="mb-1 last:mb-0">
								{group.continent && (
									<div className="px-1.5 py-1 text-xs font-semibold uppercase tracking-wide text-default-500">
										{group.continent}
									</div>
								)}
								<div className={group.continent ? 'pl-5' : ''}>
									{(group.zones || []).map(zone => {
										const zoneId = String(zone.id);
										const isSelected = value != null && String(value) === zoneId;
										return (
											<button
												key={zoneId}
												type="button"
												onClick={() => handleSelect(zoneId)}
												className={
													'w-full rounded-md px-1.5 py-1 text-left text-sm transition-colors duration-150 ' +
													(isSelected
														? 'bg-primary-100 font-medium text-primary-700'
														: 'text-foreground hover:bg-default-100')
												}
											>
												{zone.label}
											</button>
										);
									})}
								</div>
							</div>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

export default FilterTimezoneSelect;
